/**
 * texture-sample.js — het voorbeeldprofiel op /settings/textures.
 *
 * Een paar lagen waarop je ziet wat je texturen doen. Een beheerder kan ze aanpassen (texture-
 * sample-dialog); het voorbeeld wordt per bedrijf bewaard (zie samplesFrom).
 *
 * Per standaard: { layers, fillings, peilbuis, top, achtergelaten, gwsCmMv }
 *  - fillings: vulling van het boorgat, [{ van, tot, materiaal }] met VUL_*-codes (nen6693.VULMATERIAAL)
 *  - peilbuis: { filterTop, filterBottom } in cm, of null; alleen het filter doet ertoe voor de tekening
 *  - top: bovenafwerking { type, heightCm } of null; getekend als het type een eigen textuur heeft
 *  - achtergelaten: [{ van, tot, type, aard }] met typen uit nen6693.ACHTERGELATEN_TYPE
 *  - gwsCmMv: grondwaterstand in cm onder maaiveld, of null
 *
 * Een laag: { from, to, soil, admix: [{ kind, grad }], bzb: [{ code, amount }] }
 *  - soil: grondsoortcode in de standaard van het voorbeeld (NEN 6693 of NEN 5104)
 *  - admix.kind: 'siltig' | 'zandig' | 'kleiig' | 'humeus' | 'grindig'; grad: zwak … uiterst
 *  - bzb.amount: hoeveelheid zoals in een boring; bij 'dominant'/'overvloedig' (6693) of
 *    'volledig'/'uiterst' (5104) bestaat de laag uit de BZB en vult hij het hele vak
 *    (bzbFillsLayer, dezelfde regel als bij een echte boring)
 *
 * toDrawLayer() maakt er een laag van zoals de boorprofieltekening hem kent, zodat het
 * voorbeeld met precies dezelfde regels tekent als het echte boorprofiel.
 */
import { TEXTURE_CATALOG, TEXTURE_GROUP_LABELS, BODEMSOORT, bzbFillsLayer, nen6693 } from '@mv/models';
import { buildBoorprofielSvg } from './boorprofiel.js';

export const ADMIX_KINDS = ['siltig', 'zandig', 'kleiig', 'humeus', 'grindig'];
export const GRADATIONS = ['zwak', 'matig', 'sterk', 'zeer sterk', 'uiterst'];

/** Hoeveelheid van een BZB: NEN 6693 Tabel 35, en de gangbare termen uit de NEN 5104-lijst. */
export const BZB_AMOUNTS = {
  nen6693: [...nen6693.BZB_VOLUME.keys()],
  nen5104: ['sporen', 'weinig', 'matig', 'veel', 'uiterst', 'volledig'],
};
/** De hoeveelheid waarmee een BZB de laag vult, voor 'hele laag' uit een ouder bewaard voorbeeld. */
const FILL_AMOUNT = { nen6693: 'dominant', nen5104: 'volledig' };

/** Silt heet in NEN 5104 leem ('L'); 'S' is daar slib. Zie ook PREVIEW-codes in boorprofiel.js. */
const DEFAULT_LAYERS = {
  nen6693: [
    { from: 0, to: 40, soil: 'Z', admix: [{ kind: 'siltig', grad: 'matig' }], bzb: [] },
    { from: 40, to: 70, soil: null, admix: [], bzb: [{ code: 'PUI', amount: 'dominant' }] },
    { from: 70, to: 130, soil: 'K', admix: [{ kind: 'humeus', grad: 'zwak' }], bzb: [{ code: 'SCH', amount: 'weinig' }] },
    { from: 130, to: 180, soil: 'V', admix: [], bzb: [] },
    { from: 180, to: 220, soil: 'Z', admix: [{ kind: 'grindig', grad: 'zwak' }], bzb: [] },
  ],
  nen5104: [
    { from: 0, to: 40, soil: 'Z', admix: [{ kind: 'siltig', grad: 'matig' }], bzb: [] },
    { from: 40, to: 70, soil: null, admix: [], bzb: [{ code: 'PU', amount: 'volledig' }] },
    { from: 70, to: 130, soil: 'K', admix: [{ kind: 'humeus', grad: 'zwak' }], bzb: [{ code: 'SC', amount: 'weinig' }] },
    { from: 130, to: 180, soil: 'V', admix: [], bzb: [] },
    { from: 180, to: 220, soil: 'Z', admix: [{ kind: 'grindig', grad: 'zwak' }], bzb: [] },
  ],
};

/** Een peilbuis met filter onderin, afgedicht met bentoniet en filtergrind om het filter. */
const DEFAULT_FILLINGS = [
  { van: 0, tot: 130, materiaal: 'VUL_BK' },
  { van: 130, tot: 220, materiaal: 'VUL_FG' },
];
const DEFAULT_PEILBUIS = { filterTop: 150, filterBottom: 210 };

export const DEFAULT_SAMPLES = Object.fromEntries(Object.entries(DEFAULT_LAYERS).map(([std, layers]) => [
  std, {
    layers, fillings: DEFAULT_FILLINGS, peilbuis: DEFAULT_PEILBUIS, gwsCmMv: 160,
    // Het gat gedicht met een putdeksel, en onderin een achtergebleven filterdeel: zo staat elk
    // onderdeel van het boorgat in het voorbeeld en zie je wat een textuur ervan doet.
    top: { type: 'BOV_PD_GI', heightCm: 0 },
    achtergelaten: [{ van: 170, tot: 220, type: 'FIL', aard: 'achtergebleven' }],
  },
]));

/** Elke code uit de catalogus met zijn eigen naam (een reeks als 'alle' asfalt wordt uitgevouwen). */
function codesFor(standard, category) {
  const out = [];
  for (const e of TEXTURE_CATALOG) {
    if (e.standard !== standard || e.category !== category) continue;
    e.codes.forEach((code, i) => out.push({ code, label: e.memberLabels[i] || e.label, family: e.family }));
  }
  return out;
}

const cache = new Map();
const cached = (key, make) => { if (!cache.has(key)) cache.set(key, make()); return cache.get(key); };

/** Grondsoorten van een standaard, op naam. */
export const soilOptions = (standard) => cached(`soil:${standard}`, () =>
  codesFor(standard, 'soil').sort((a, b) => a.label.localeCompare(b.label, 'nl')));

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * De keuzelijst Hoofdbestanddeel, precies zoals in de lagen van een boring:
 *  - NEN 6693: de groepen uit nen6693.HOOFDBESTANDDEEL_GROEPEN (minerale, organische en
 *    bijzondere laagtypen), met rots als één keuze -- het gesteentetype zelf kies je in een
 *    boring apart, en voor de textuur maakt het niet uit (alle gesteenten delen één instelling).
 *  - NEN 5104: de SIKB-lijst in zijn eigen volgorde.
 * Waarde = de code zoals een boring hem opslaat. [{ label|null, options: [{ code, label }] }]
 */
export const soilGroups = (standard) => cached(`soilGroups:${standard}`, () => {
  if (standard === 'nen5104') return [{ label: null, options: [...BODEMSOORT].map(([label, code]) => ({ code, label })) }];
  const codeOf = (term) => nen6693.GRONDSOORT.get(term) || nen6693.LAAGTYPE_SPECIAAL.get(term) || null;
  return nen6693.HOOFDBESTANDDEEL_GROEPEN.map((g) => ({
    label: g.label,
    options: g.items.map((o) => ({ code: codeOf(o.term), label: cap(o.label) })).filter((o) => o.code),
  }));
});

/** BZB's van een standaard, per familie (zelfde volgorde als de tabel). */
export const bzbGroups = (standard) => cached(`bzb:${standard}`, () => {
  const byFamily = new Map();
  for (const o of codesFor(standard, 'bzb')) {
    if (!byFamily.has(o.family)) byFamily.set(o.family, []);
    byFamily.get(o.family).push(o);
  }
  const order = Object.keys(TEXTURE_GROUP_LABELS);
  return [...byFamily.entries()]
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([family, options]) => ({ label: TEXTURE_GROUP_LABELS[family] || family, options }));
});

const labelOf = (list, code) => list.find((o) => o.code === code)?.label || code || '';
export const soilLabel = (standard, code) => labelOf(soilOptions(standard), code);
export const bzbLabel = (standard, code) => labelOf(bzbGroups(standard).flatMap((g) => g.options), code);

/** De laag zoals boorprofiel.js (layerTextures / buildBoorprofielSvg) hem verwacht. */
export function toDrawLayer(l, standard) {
  const admix = (l.admix || []).filter((a) => a.kind).map((a) => ({ label: a.kind, grad: a.grad || 'zwak' }));
  const bzb = (l.bzb || []).filter((b) => b.code).map((b) => ({ code: b.code, label: bzbLabel(standard, b.code), fillsLayer: bzbFillsLayer(b.amount) }));
  return {
    from: Number(l.from) || 0, to: Number(l.to) || 0,
    soilCode: l.soil || null, soilLabel: soilLabel(standard, l.soil), isRock: false,
    // Zoals in het echte boorprofiel: de BZB's op de tweede regel van de beschrijving.
    admix, bzb, line2: bzb.map((b) => b.label).join(', '),
  };
}

/** Hoogte van de lagenkolom in het voorbeeld (px); op de pagina begrensd door het venster. */
export const SAMPLE_PROFILE_HEIGHT = 680;

/**
 * Het voorbeeld als echt boorprofiel (buildBoorprofielSvg), zonder legenda: de texturentabel is
 * de legenda al. Eén functie voor de pagina en 'Voorbeeld aanpassen', zodat die twee altijd
 * hetzelfde tekenen. Lege string als er geen lagen zijn.
 */
/** '' of geen getal = geen grondwater. */
const gwsOf = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v));

export function sampleProfileSvg(sample, standard, textures, heightPx = SAMPLE_PROFILE_HEIGHT) {
  const layers = (sample?.layers || []).map((r) => toDrawLayer(r, standard)).filter((l) => l.to > l.from);
  if (!layers.length) return '';
  const pb = sample.peilbuis;
  return buildBoorprofielSvg({
    header: { name: 'Voorbeeld', maaiveld: null, referencePlane: null, boormeester: null },
    layers,
    fillings: (sample.fillings || []).filter((f) => Number(f.tot) > Number(f.van))
      .map((f) => ({ van: Number(f.van), tot: Number(f.tot), code: f.materiaal })),
    achtergelaten: (sample.achtergelaten || []).filter((o) => Number(o.tot) > Number(o.van))
      .map((o) => ({ van: Number(o.van), tot: Number(o.tot), aard: o.aard, type: o.type || null, typeLabel: nen6693.achtergelatenTypeLabel(o.type) })),
    bovenafwerking: sample.top?.type ? { type: sample.top.type, label: nen6693.bovenafwerkingLabel(sample.top.type) } : null,
    peilbuizen: pb ? [{ filterTop: Number(pb.filterTop) || null, filterBottom: Number(pb.filterBottom) || null }] : [],
    gwsCmMv: gwsOf(sample.gwsCmMv),
    boordiepteCm: Math.max(...layers.map((l) => l.to)),
    standard, textures: textures || null,
    profileHeightPx: heightPx,
    hideLegend: true,
  });
}

/** Een ouder voorbeeld had per BZB een vinkje 'hele laag' in plaats van een hoeveelheid. */
const upgrade = (l, std) => ({
  ...l,
  bzb: (l.bzb || []).map((b) => (b.amount ? b : { code: b.code, amount: b.fills ? FILL_AMOUNT[std] : 'weinig' })),
});

/**
 * Het voorbeeld van het bedrijf zoals de server het geeft (GET /api/company-textures → `sample`,
 * per norm), aangevuld met het standaardvoorbeeld voor een norm die het bedrijf niet heeft
 * aangepast. Opgeslagen per bedrijf (companies.texture_sample), nooit in de browser: wat het
 * ene bedrijf aanpast mag een ander bedrijf niet zien.
 */
export function samplesFrom(raw) {
  const pick = (std) => {
    const sample = raw && typeof raw === 'object' ? raw[std] : null;
    if (!sample || !Array.isArray(sample.layers) || !sample.layers.length) return DEFAULT_SAMPLES[std];
    return {
      layers: sample.layers.map((l) => upgrade(l, std)),
      fillings: Array.isArray(sample.fillings) ? sample.fillings : [],
      peilbuis: sample.peilbuis ?? null,
      top: sample.top ?? null,
      achtergelaten: Array.isArray(sample.achtergelaten) ? sample.achtergelaten : [],
      gwsCmMv: Number.isFinite(sample.gwsCmMv) ? sample.gwsCmMv : null,
    };
  };
  return { nen5104: pick('nen5104'), nen6693: pick('nen6693') };
}
